import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AssetUploadProperty, UploadCollection} from "../upload.model";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatInput} from "@angular/material/input";
import {FullHeighDirective} from '../../common/full-heigh.directive';
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {UploadStateService} from "../upload-state.service";
import {TranslatePipe} from "@ngx-translate/core";
import {MatCheckbox} from "@angular/material/checkbox";
import {AssetProperties, AssetProperty} from "../../assets/asset.model";
import {MatTooltip} from '@angular/material/tooltip';
import {buildPropertyUsages, PropertyUsages, union} from "./upload-property-utils";
import {MatSlideToggle, MatSlideToggleChange} from "@angular/material/slide-toggle";
import {calculateEligibleFormProperties} from "../upload-asset/upload-form-utils";
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {OrderBy} from "../../assets/order-by";
import {Collection} from "../../collection/collection.model";
import {CollectionService} from "../../collection/collection.service";
import {debounceTime, distinctUntilChanged, Subscription} from "rxjs";
import {UserStateService} from "../../user/user.state.service";
import {User} from "../../user/user";

@Component({
    selector: 'app-upload-master-properties',
    imports: [
        MatFormFieldModule,
        MatOption,
        MatSelect,
        ReactiveFormsModule,
        MatInput,
        FullHeighDirective,
        MatIcon,
        TranslatePipe,
        MatIconButton,
        MatCheckbox,
        MatTooltip,
        MatSlideToggle,
        FormsModule,
        MatAutocomplete,
        MatAutocompleteTrigger,
    ],
    templateUrl: './upload-master-properties.component.html',
    styleUrl: './upload-master-properties.component.scss'
})
export class UploadMasterPropertiesComponent implements OnInit, OnChanges, OnDestroy {

    @Input({required: true}) properties!: AssetUploadProperty[];
    private chosenProperties: AssetProperties[] = [];

    @Output() propertyChange: EventEmitter<AssetProperty> = new EventEmitter<AssetProperty>();
    @Output() visibilityChange: EventEmitter<AssetUploadProperty> = new EventEmitter<AssetUploadProperty>();
    disabled = true;
    isPropertyVisibleALL: boolean = false;
    propertiesForm: FormGroup = new FormGroup({});
    propertyUsages: PropertyUsages = {};
    propertiesAdditive = false;
    eligibleProperties: AssetUploadProperty[] = [];

    collectionForm: FormGroup
    collections: Collection[] = [];
    user: User  | null = null;
    userSubscription: Subscription | undefined;
    private searchSubscription: Subscription | undefined;
    searchCollectionSetupDone = false;

    constructor(
        private uploadStateService: UploadStateService,
        private collectionService: CollectionService,
        private userStateService: UserStateService,
        private fb: FormBuilder
    ) {
        this.collectionForm = fb.group({
            search: ['', []],
            collectionId: ['', []],
        })
    }

    get mode() {
        return this.uploadStateService.mode;
    }

    ngOnInit(): void {
        this.setUpPropertiesForm();
        // this.setUpCollectionForm();
        this.userSubscription = this.userStateService.user$.subscribe(user => {
            this.user = user;
            if (this.user && !this.searchCollectionSetupDone) {
                this.setUpCollectionForm();
            }
        })
        this.uploadStateService.mode$.subscribe({
            next: mode => {
                if (mode === 'inactive') {
                    this.clearCollection();
                }
            }
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['properties']) {
            this.setUpPropertiesForm();
        }
    }

    assetPropertiesChanged(assetProperties: AssetProperties[]) {
        this.chosenProperties = assetProperties;
        this.setUpPropertiesForm();
    }

    setUpPropertiesForm() {
        this.propertyUsages = buildPropertyUsages(this.properties, this.chosenProperties);
        this.eligibleProperties = calculateEligibleFormProperties(this.properties, union(this.chosenProperties));
        this.propertiesForm = this.buildForm(this.eligibleProperties, this.propertyUsages);
    }

    private buildForm(properties: AssetUploadProperty[], usages: PropertyUsages): FormGroup {
        let formGroup = new FormGroup({});
        for (let property of properties) {
            let control = this.buildFormControl(property, this.disabled, usages[property.name]);
            control.valueChanges.subscribe({
                next: (value) => {
                    if (property.isMultiple && typeof value === 'string') {
                        value = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                    }
                    this.propertyChange.emit({key: property.name, value: value});
                }
            });
            formGroup.addControl(property.name, control);
        }
        return formGroup;
    }

    private buildFormControl(property: AssetUploadProperty, disable: boolean, usages?: {
        [value: string]: number
    }): FormControl {
        let disabled = property.isReadonly || disable;
        switch (property.type) {
            case 'text':
            case 'textarea':
            case 'date':
                return new FormControl({value: '', disabled}, {updateOn: 'blur'});
            case 'checkbox':
                let selectedValues: string[] = [];
                for (let usage in usages) {
                    if (usages[usage] > 0) {
                        selectedValues.push(usage);
                    }
                }
                return new FormControl({value: selectedValues, disabled});
            case 'boolean':
                return new FormControl({value: false, disabled});
            default:
               // console.log('Unknown property type', property);
                throw new Error('Unknown property type');
        }
    }

    isPropertyVisible(prop: AssetUploadProperty): boolean {
        return this.uploadStateService.visibleProperties.includes(prop.name);
    }

    toggleVisibility(prop: AssetUploadProperty) {
        if (this.uploadStateService.visibleProperties.includes(prop.name)) {
            this.uploadStateService.removeVisibleProperty(prop.name);
        } else {
            this.uploadStateService.addVisibleProperty(prop.name);
        }
    }

    toggleAllVisibilities() {
        this.isPropertyVisibleALL = !this.isPropertyVisibleALL;
        if (this.isPropertyVisibleALL) {
            for (const prop of this.properties) {
                if (!this.uploadStateService.visibleProperties.includes(prop.name)) {
                    this.uploadStateService.addVisibleProperty(prop.name);
                }
            }
        } else {
            for (const prop of this.properties) {
                if (this.uploadStateService.visibleProperties.includes(prop.name)) {
                    this.uploadStateService.removeVisibleProperty(prop.name);
                }
            }
        }

        // todo
    }

    clearValue(prop: AssetUploadProperty) {
        let control = this.propertiesForm.get(prop.name);
        if (control) {
            switch (prop.type) {
                case 'text':
                case 'textarea':
                case 'date':
                    control.setValue('');
                    break;
                case 'checkbox':
                    control.setValue([]);
                    break;
                case 'boolean':
                    control.setValue(false);
                    break;
            }
        }
    }

    clearAllValues() {
        for (const property of this.properties) {
            this.clearValue(property);
        }
    }

    toggleAdditive($event: MatSlideToggleChange) {
        this.propertiesAdditive = $event.checked
    }


    onMultiValueKeyUp($event: KeyboardEvent) {
        if ($event.key === 'Enter') {
            let target = $event.target as HTMLInputElement;
            target.blur();
            this.focusNextMasterProperty(target);
        }
    }

    private focusNextMasterProperty(target: HTMLInputElement) {
        let container = target.closest('.master-property');
        let next = container?.nextElementSibling;
        if (next) {
            let input = next.querySelector('input');
            if (input) {
                input.focus();
            }
        }
    }

    private setUpCollectionForm() {
        this.searchSubscription = this.collectionForm.controls['search'].valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged()
            ).subscribe(searchCollection => {
                if (typeof searchCollection === 'string') {
                    this.getCollections(searchCollection);
                }
            });
        this.getMyCollections();
        this.searchCollectionSetupDone = true;
    }

    private getCollections(searchTerm: string) {
        this.collectionService.getCollections(undefined, 'write', searchTerm, OrderBy.created, 'desc', 1, 10).subscribe(collections => {
            this.collections = collections.data;
        })
    }

    private getMyCollections() {
        this.collectionService.getCollections('my', 'write', '', OrderBy.created, 'desc', 1, 10).subscribe(collections => {
            this.collections = collections.data;
        })
    }

    selectCollection(e: MatAutocompleteSelectedEvent) {
        const col: Collection = e.option.value;
        this.collectionForm.patchValue({
            collectionId: col.uuid
        })
    }

    displayFn(coll: Collection) {
        return coll && coll.name ? coll.name : '';
    }

    clearCollection() {
        this.collectionForm.patchValue({
            search: '',
            collectionId: ''
        })
    }

    getUploadCollection(): UploadCollection {
        return {
            uuid: this.collectionForm.get('collectionId')?.value,
            name: this.collectionForm.get('search')?.value,
        }
    }

    ngOnDestroy() {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
    }
}
